import { Link } from "@tanstack/react-router";
import { MapPin, ArrowRight } from "lucide-react";

const ALL_CITIES: { to: string; name: string }[] = [
  { to: "/san-antonio", name: "San Antonio" },
  { to: "/austin", name: "Austin" },
  { to: "/seguin", name: "Seguin" },
  { to: "/new-braunfels", name: "New Braunfels" },
  { to: "/boerne", name: "Boerne" },
  { to: "/kerrville", name: "Kerrville" },
  { to: "/fredericksburg", name: "Fredericksburg" },
  { to: "/corpus-christi", name: "Corpus Christi" },
];

const ALL_SERVICES: { to: string; name: string; desc: string }[] = [
  { to: "/louvered-pergolas", name: "Louvered Pergolas", desc: "Motorized aluminum opening-roof outdoor rooms" },
  { to: "/awnings", name: "Custom Awnings", desc: "Welded aluminum + Sunbrella canvas" },
  { to: "/pergola-covers", name: "Patio Covers", desc: "Residential & commercial shade" },
  { to: "/sail-shades", name: "Sail Shades", desc: "Tensioned shade structures" },
  { to: "/curtains", name: "Drop Curtains", desc: "Outdoor & roll curtains" },
  { to: "/covers-tops", name: "Covers & Tops", desc: "BBQ, equipment & custom covers" },
  { to: "/schools-athletics", name: "Schools & Athletics", desc: "Pit covers, windscreens, goalpost pads" },
  { to: "/trailer-covers", name: "Trailer Covers", desc: "Stock & cattle trailer covers" },
  { to: "/pool-spa", name: "Pool & Spa", desc: "Pool covers and spa shade" },
  { to: "/custom-fabrication", name: "Custom Fabrication", desc: "Welding & custom sewing" },
];

interface RelatedLinksProps {
  /** Path of the current service page (e.g. "/awnings") — excluded from related services */
  currentPath: string;
  /** Service name for the heading text (e.g. "Awnings") */
  serviceName: string;
}

export function RelatedLinks({ currentPath, serviceName }: RelatedLinksProps) {
  const related = ALL_SERVICES.filter((s) => s.to !== currentPath).slice(0, 4);

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Cities served */}
        <div>
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Service Areas</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            {serviceName} Across Central & South Texas
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            We pattern, fabricate, and install across Central Texas, the Hill Country, and the Gulf Coast.
            Click your city for local project examples and service details.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {ALL_CITIES.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group flex items-center justify-between border border-navy-deep/15 bg-cream px-4 py-3 transition hover:border-navy-deep hover:bg-navy-deep hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-red group-hover:text-white" />
                  <span className="font-display text-base text-navy-deep group-hover:text-white">
                    {serviceName} in {c.name}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </div>

        {/* Related services */}
        <div className="mt-16">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Related Services</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Pairs Well With
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group block border-l-4 border-red bg-cream p-5 shadow-card transition hover:bg-navy-deep hover:text-white"
              >
                <div className="font-display text-lg font-bold text-navy-deep group-hover:text-white">
                  {s.name}
                </div>
                <div className="mt-1 text-sm text-muted-foreground group-hover:text-white/80">
                  {s.desc}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 font-display text-xs uppercase tracking-[0.2em] text-red group-hover:text-white">
                  Learn more <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Projects + shop links */}
        <div className="mt-16 grid gap-4 md:grid-cols-2">
          {currentPath !== "/dog-boxes" && (
            <Link
              to="/projects"
              className="group flex items-center justify-between border border-navy-deep bg-navy-deep px-6 py-5 text-white transition hover:bg-red"
            >
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-white/70">Project Gallery</div>
                <div className="mt-1 font-display text-xl font-bold">See {serviceName} we've built</div>
              </div>
              <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
            </Link>
          )}
          <Link
            to="/shop"
            className="group flex items-center justify-between border border-navy-deep bg-cream px-6 py-5 text-navy-deep transition hover:bg-navy-deep hover:text-white"
          >
            <div>
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">Shop Supplies</div>
              <div className="mt-1 font-display text-xl font-bold">Hardware, thread, fabrics &amp; more</div>
            </div>
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
